<template>
  <!-- placement="floating": barra fixa no canto superior direito (desktop).
       placement="sidebar": dentro do Navbar no mobile, acima da versão —
       avatar + nome à esquerda, Novidades à direita (flex-row-reverse), e os
       menus abrem pra cima. Com o Navbar recolhido (60px), avatar e sino
       ficam empilhados e o nome some. -->
  <div
    ref="rootRef"
    :class="isSidebar
      ? (collapsed ? 'w-full flex flex-col-reverse items-center gap-1' : 'w-full flex flex-row-reverse items-center gap-1')
      : `fixed top-4 right-4 z-40 flex items-center gap-1 bg-white dark:bg-zinc-900
         border border-slate-200/80 dark:border-zinc-800 rounded-2xl
         shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.06)]
         dark:shadow-[0_8px_40px_-6px_rgba(0,0,0,0.55)]
         px-1.5 py-1.5`"
  >
    <!-- ── Novidades (atualizações) ─────────────────────────────── -->
    <!-- Na barra lateral (mobile) o sino some — Novidades vira uma opção do
         menu do perfil (ver "Novidades" abaixo), e o avatar ganha um ponto
         quando há novidade não lida. O painel (Teleport) continua aqui. -->
    <div ref="updatesWrapperRef" class="relative">
      <button
        v-if="!isSidebar"
        @click="toggleUpdates"
        title="Novidades"
        class="relative p-2 rounded-xl text-slate-500 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
      >
        <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <span
          v-if="updatesUnreadCount > 0"
          class="absolute top-1 right-1 min-w-[15px] h-[15px] px-[3px] rounded-full bg-primary-500 text-white flex items-center justify-center leading-none border border-white dark:border-zinc-900"
          style="font-size:9px"
        >{{ updatesUnreadCount > 9 ? '9+' : updatesUnreadCount }}</span>
      </button>

      <Teleport to="body">
        <Transition :name="isSidebar ? 'topbar-drop-up' : 'topbar-drop'">
          <div
            v-if="updatesOpen"
            ref="updatesPanelRef"
            :style="updatesPanelStyle"
            class="fixed z-[9999] w-80 max-w-[calc(100vw-2rem)]
                   rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900
                   shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.06)]
                   dark:shadow-[0_8px_40px_-6px_rgba(0,0,0,0.55)]
                   overflow-hidden flex flex-col"
          >
            <div class="px-4 py-3 border-b border-slate-100 dark:border-zinc-800 flex-shrink-0">
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">Novidades</p>
            </div>
            <div class="overflow-y-auto scrollbar-thin max-h-96">
              <NotificationsList :notifications="updateNotifications" @select="onSelectUpdate" />
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>

    <div v-if="!isSidebar" class="w-px h-6 bg-slate-200 dark:bg-zinc-700 mx-0.5" />

    <!-- ── Usuário (avatar + nome) ───────────────────────────────── -->
    <div ref="userWrapperRef" class="relative" :class="{ 'flex-1 min-w-0': showSidebarName }">
      <button
        @click="toggleUserMenu"
        :title="isSidebar && collapsed ? userDisplayName : undefined"
        class="flex items-center gap-2 pl-1 py-1 rounded-xl hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
        :class="showSidebarName ? 'w-full pr-2.5' : isSidebar ? 'pr-1' : 'pr-1 lg:pr-2.5'"
      >
        <div class="relative flex-shrink-0">
          <div class="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center"
               style="background: linear-gradient(135deg, #ff7550, #ff4d18)">
            <img v-if="avatarSrc" :src="avatarSrc" :alt="userDisplayName" class="w-full h-full object-cover" />
            <span v-else class="text-white font-bold" style="font-size:11px">{{ userInitials }}</span>
          </div>
          <!-- Novidade não lida (barra lateral, onde o sino virou item do menu) -->
          <span v-if="isSidebar && updatesUnreadCount > 0"
            class="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary-500 border-2 border-white dark:border-zinc-900"
            aria-hidden="true" />
        </div>
        <span class="text-sm font-medium text-zinc-800 dark:text-zinc-100 truncate"
              :class="showSidebarName ? 'flex-1 min-w-0 text-left' : isSidebar ? 'hidden' : 'hidden lg:inline max-w-[16rem]'">{{ userDisplayName }}</span>
        <svg class="w-3.5 h-3.5 text-slate-400 dark:text-zinc-600 flex-shrink-0"
             :class="showSidebarName ? 'rotate-180' : isSidebar ? 'hidden' : 'hidden lg:block'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <Teleport to="body">
        <Transition :name="isSidebar ? 'topbar-drop-up' : 'topbar-drop'">
          <div
            v-if="userMenuOpen"
            ref="userMenuRef"
            :style="userMenuStyle"
            class="fixed z-[9999] min-w-[13rem] rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900
                   shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.06)]
                   dark:shadow-[0_8px_40px_-6px_rgba(0,0,0,0.55)]
                   overflow-hidden py-1"
          >
            <button
              @click="onToggleTheme"
              class="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <svg v-if="isDarkMode" class="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <svg v-else class="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <span class="flex-1 text-left">{{ isDarkMode ? m.actions.lightMode : m.actions.darkMode }}</span>
            </button>
            <button
              @click="onOpenProfile"
              class="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <svg class="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span class="flex-1 text-left">Meu perfil</span>
            </button>
            <!-- Folga / "só à tarde" de um profissional numa data (ADMIN, ou o
                 profissional com a permissão "Ajustar a própria disponibilidade").
                 Abre o modal na Agenda via /appointments?disponibilidade=1. -->
            <button
              v-if="canAdjustAvailability"
              @click="onOpenAvailability"
              class="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <svg class="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="flex-1 text-left">Ajustar disponibilidade</span>
            </button>
            <!-- Novidades — só na barra lateral (mobile), onde substitui o sino. -->
            <button
              v-if="isSidebar"
              @click="onOpenUpdatesFromMenu"
              class="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <svg class="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span class="flex-1 text-left">Novidades</span>
              <span v-if="updatesUnreadCount > 0"
                class="min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none">
                {{ updatesUnreadCount > 9 ? '9+' : updatesUnreadCount }}
              </span>
            </button>
            <div class="my-1 h-px bg-slate-100 dark:bg-zinc-800" />
            <button
              @click="onLogout"
              class="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-danger-500 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-950/30 transition-colors"
            >
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span class="flex-1 text-left">{{ m.actions.logout }}</span>
            </button>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>

  <!-- ── Profile Edit Modal (movido de Navbar.vue) ────────────────── -->
  <ProfileModal :open="staffProfileOpen" @close="staffProfileOpen = false" />

  <Modal :open="profileOpen" title="Editar perfil" size="sm" variant="gray" @close="closeProfile">
    <form id="topbar-profile-form" class="modal-form-gray" @submit.prevent="saveProfile">
      <div class="rounded-xl border border-slate-200 dark:border-zinc-700 p-4 space-y-4">
        <div class="form-group">
          <label class="form-label">Nome</label>
          <input v-model="profileForm.name" type="text" required class="form-input" placeholder="Seu nome" />
        </div>
        <div class="form-group">
          <label class="form-label">Email</label>
          <input v-model="profileForm.email" type="email" required class="form-input" placeholder="seu@email.com" />
        </div>
        <div class="form-group">
          <label class="form-label">Nova senha <span class="text-slate-400 font-normal">(deixe em branco para manter)</span></label>
          <input v-model="profileForm.password" type="password" class="form-input" placeholder="Nova senha" minlength="8" maxlength="16" />
          <PasswordRequirements v-if="profileForm.password" :password="profileForm.password" />
        </div>
      </div>
      <p v-if="profileError" class="text-xs text-danger-500 dark:text-danger-400 mt-3">{{ profileError }}</p>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary px-4 py-2 text-sm" @click="closeProfile">Cancelar</button>
        <button type="submit" form="topbar-profile-form" class="btn-primary px-4 py-2 text-sm" :disabled="profileSaving">
          <span v-if="profileSaving" class="loading-spinner w-3.5 h-3.5 mr-1" />
          Salvar
        </button>
      </div>
    </template>
  </Modal>

  <!-- ── Detalhe de notificação de nova versão (tenant não acessa
       /novidades, ver useNotifications.js/markAsRead) — movido de
       Navbar.vue junto com o sino geral. ────────────────────────── -->
  <Modal :open="releaseDetailModal.open" :title="releaseDetailModal.title" size="md" variant="gray" @close="releaseDetailModal.open = false">
    <div class="space-y-3">
      <div class="flex items-center gap-2.5 flex-wrap">
        <span class="text-xs text-slate-400 dark:text-zinc-500">Disponibilizada em {{ releaseDetailModal.date }}</span>
        <span class="text-xs font-mono font-semibold px-2 py-1 rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400">
          v{{ releaseDetailModal.version }}
        </span>
      </div>
      <ul class="space-y-2">
        <li v-for="(line, index) in releaseNotesLines" :key="index" class="flex items-center gap-3">
          <span class="w-5 h-5 rounded-full bg-primary-50 dark:bg-primary-950/40 text-primary-600 flex items-center justify-center shrink-0">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <span class="text-sm text-zinc-700 dark:text-zinc-300">{{ line }}</span>
        </li>
      </ul>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useDarkMode } from '../../composables/useDarkMode.js'
import { useToast } from '../../composables/useToast'
import { useNotifications } from '../../composables/useNotifications.js'
import { useLocaleStore } from '../../stores/locale.js'
import { getMessages } from '../../i18n/index.js'
import apiClient from '../../api/client.js'
import Modal from '../ui/Modal.vue'
import ProfileModal from './ProfileModal.vue'
import PasswordRequirements from '../common/PasswordRequirements.vue'
import NotificationsList from '../common/NotificationsList.vue'
import { mediaUrl, API_ORIGIN } from '../../utils/mediaUrl.js'
import { isPasswordValid, PASSWORD_REQUIREMENTS_MESSAGE } from '../../utils/passwordPolicy'

interface AuthUser { id: number; name: string; email: string; role: string; avatar_url?: string | null }

const props = withDefaults(
  defineProps<{ placement?: 'floating' | 'sidebar'; collapsed?: boolean }>(),
  { placement: 'floating', collapsed: false },
)
const isSidebar = computed(() => props.placement === 'sidebar')
const showSidebarName = computed(() => isSidebar.value && !props.collapsed)

const router = useRouter()
const authStore = useAuthStore()
const { isDarkMode, toggleDarkMode } = useDarkMode()
const { success } = useToast()
const notif = useNotifications()
// releaseDetailModal (reactive) desestruturado direto funciona igual no
// template; releaseNotesLines é um computed — precisa estar no topo do
// setup pra ser desembrulhado automaticamente no template (acessar via
// "notif.releaseNotesLines" devolveria o ref cru, não o array).
const { releaseDetailModal, releaseNotesLines } = notif

const rootRef = ref<HTMLElement | null>(null)

const localeStore = useLocaleStore()
const m = computed(() => getMessages(localeStore.locale))

const user = computed<AuthUser | null>(() => authStore.user)

const userDisplayName = computed(() => user.value?.name || '')
const userInitials = computed(() => {
  if (!user.value?.name) return ''
  return user.value.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()
})

const API_BASE = API_ORIGIN
const avatarSrc = computed(() => mediaUrl(user.value?.avatar_url || null, API_BASE))

// Assina o mesmo polling de notificações que a Navbar (ver useNotifications.js)
onMounted(() => notif.startPolling())
onUnmounted(() => notif.stopPolling())

// ─── Novidades (atualizações) ───────────────────────────────────────
// Recorte das notificações gerais (mesma fonte da Navbar) só com as de
// nova versão publicada (notification.link === '/novidades' — ver
// notifyAllStaff em src/utils/notifications.js).
const updateNotifications = computed(() =>
  notif.notifications.value.filter((n: any) => n.link === '/novidades').slice(0, 8)
)
const updatesUnreadCount = computed(() =>
  updateNotifications.value.filter((n: any) => !n.read_at).length
)

const updatesOpen = ref(false)
const updatesWrapperRef = ref<HTMLElement | null>(null)
const updatesPanelRef = ref<HTMLElement | null>(null)
const updatesPanelStyle = ref<Record<string, string>>({})

function updateUpdatesPosition() {
  // Na barra lateral (rodapé do Navbar) não há espaço embaixo: abre pra cima,
  // alinhado à esquerda da barra (a largura do painel passa da do Navbar).
  // Lá o sino não existe (virou item do menu) — a referência é a barra toda.
  if (isSidebar.value) {
    const root = rootRef.value?.getBoundingClientRect()
    if (!root) return
    updatesPanelStyle.value = { bottom: `${window.innerHeight - root.top + 8}px`, left: `${root.left}px` }
    return
  }
  const el = updatesWrapperRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  updatesPanelStyle.value = { top: `${rect.bottom + 8}px`, right: `${window.innerWidth - rect.right}px` }
}
function toggleUpdates() {
  if (updatesOpen.value) { updatesOpen.value = false; return }
  userMenuOpen.value = false
  updateUpdatesPosition()
  updatesOpen.value = true
}
const canAdjustAvailability = computed(() => {
  const role = authStore.user?.role
  if (!authStore.hasFeature('APPOINTMENTS')) return false
  return role === 'ADMIN' || (role === 'PROFESSIONAL' && authStore.can('agenda.time_off_own'))
})
function onOpenAvailability() {
  userMenuOpen.value = false
  router.push({ path: '/appointments', query: { disponibilidade: '1' } })
}
// Item "Novidades" do menu do perfil (barra lateral): fecha o menu e abre o
// painel de novidades no lugar dele.
function onOpenUpdatesFromMenu() {
  userMenuOpen.value = false
  updateUpdatesPosition()
  updatesOpen.value = true
}
async function onSelectUpdate(notification: any) {
  updatesOpen.value = false
  await notif.markAsRead(notification, user.value?.role, router)
}

// ─── Menu do usuário (tema / perfil / sair) ─────────────────────────
const userMenuOpen = ref(false)
const userWrapperRef = ref<HTMLElement | null>(null)
const userMenuRef = ref<HTMLElement | null>(null)
const userMenuStyle = ref<Record<string, string>>({})

// Largura/posição vêm da barra inteira (rootRef), não só do bloco do
// avatar — o usuário quer o menu ocupando também o espaço do sino de
// Novidades, não só embaixo do avatar+nome.
function updateUserMenuPosition() {
  const el = rootRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  if (isSidebar.value) {
    // Recolhido, a barra tem ~44px — o menu usa a própria min-w (13rem).
    userMenuStyle.value = {
      bottom: `${window.innerHeight - rect.top + 8}px`,
      left: `${rect.left}px`,
      ...(props.collapsed ? {} : { width: `${rect.width}px` }),
    }
    return
  }
  userMenuStyle.value = {
    top: `${rect.bottom + 8}px`,
    right: `${window.innerWidth - rect.right}px`,
    width: `${rect.width}px`,
  }
}
function toggleUserMenu() {
  if (userMenuOpen.value) { userMenuOpen.value = false; return }
  updatesOpen.value = false
  updateUserMenuPosition()
  userMenuOpen.value = true
}

// Não fecha o menu — o usuário pode querer alternar claro/escuro mais de
// uma vez seguida pra comparar antes de decidir.
function onToggleTheme() {
  toggleDarkMode()
}

function onLogout() {
  userMenuOpen.value = false
  authStore.logout().then(() => router.push('/login'))
}

function onClickOutside(e: MouseEvent) {
  const target = e.target as Node
  if (updatesOpen.value &&
      !updatesWrapperRef.value?.contains(target) &&
      !updatesPanelRef.value?.contains(target)) {
    updatesOpen.value = false
  }
  if (userMenuOpen.value &&
      !userWrapperRef.value?.contains(target) &&
      !userMenuRef.value?.contains(target)) {
    userMenuOpen.value = false
  }
}
function onScrollOrResize() {
  if (updatesOpen.value) updateUpdatesPosition()
  if (userMenuOpen.value) updateUserMenuPosition()
}
onMounted(() => {
  document.addEventListener('mousedown', onClickOutside)
  window.addEventListener('scroll', onScrollOrResize, { capture: true, passive: true })
  window.addEventListener('resize', onScrollOrResize, { passive: true })
})
onUnmounted(() => {
  document.removeEventListener('mousedown', onClickOutside)
  window.removeEventListener('scroll', onScrollOrResize, { capture: true })
  window.removeEventListener('resize', onScrollOrResize)
})

// ─── Edição de perfil (movida de Navbar.vue) ────────────────────────
const profileOpen = ref(false)
const profileSaving = ref(false)
const profileError = ref('')
const profileForm = reactive({ name: '', email: '', password: '' })

// Equipe do estabelecimento abre o cadastro completo (ProfileModal.vue);
// o dono da plataforma, sem cadastro de estabelecimento, segue no modal
// simples abaixo (nome/e-mail/senha).
const isTenantStaff = computed(() => ['ADMIN', 'RECEPTIONIST', 'PROFESSIONAL'].includes(user.value?.role || ''))
const staffProfileOpen = ref(false)

function onOpenProfile() {
  userMenuOpen.value = false
  if (isTenantStaff.value) {
    staffProfileOpen.value = true
    return
  }
  profileForm.name = user.value?.name || ''
  profileForm.email = user.value?.email || ''
  profileForm.password = ''
  profileError.value = ''
  profileOpen.value = true
}
function closeProfile() {
  profileOpen.value = false
}
async function saveProfile() {
  profileError.value = ''
  if (profileForm.password && !isPasswordValid(profileForm.password)) {
    profileError.value = PASSWORD_REQUIREMENTS_MESSAGE
    return
  }
  profileSaving.value = true
  try {
    const payload: Record<string, string> = {
      name: profileForm.name,
      email: profileForm.email,
    }
    if (profileForm.password) payload.password = profileForm.password

    const res = await apiClient.put(`/users/${user.value?.id}`, payload)
    if (authStore.user) {
      authStore.user.name = res.data.data?.name || profileForm.name
      authStore.user.email = res.data.data?.email || profileForm.email
    }
    success('Perfil atualizado', 'Suas informações foram salvas com sucesso.')
    closeProfile()
  } catch (err: any) {
    profileError.value = err?.response?.data?.error || 'Erro ao salvar perfil'
  } finally {
    profileSaving.value = false
  }
}
</script>

<style scoped>
.topbar-drop-enter-active, .topbar-drop-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.topbar-drop-enter-from, .topbar-drop-leave-to { opacity: 0; transform: translateY(-4px); }
.topbar-drop-up-enter-active, .topbar-drop-up-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.topbar-drop-up-enter-from, .topbar-drop-up-leave-to { opacity: 0; transform: translateY(4px); }

/* Mesma troca de paleta usada em outros modais (ver Modal.vue variant="gray")
   — fundo azulado padrão de .form-input/.form-select (style.css) vira cinza
   neutro dentro do modal de perfil. */
.modal-form-gray :deep(.form-input),
.modal-form-gray :deep(.form-select),
.modal-form-gray :deep(.form-textarea) {
  @apply dark:bg-zinc-800 dark:border-zinc-700;
}
</style>
